/**
 * The split-body afterimage in the sample look (lane FX-PORT; the user, 10-11: "the selling point of our skill FX"):
 * a copy of the figure in the pose it was in, as the flat two-tone steel qi silhouette (`ghostOf`), solid for 35 % of
 * its life, then 0.2, then 0.08 — drawn THROUGH a pixel pen like the sample pages (tools/fxSamples/skills.ts, taiji.ts),
 * so the picture sits on the scene grid with its outline and the faint levels become a checker dither. It does so
 * whatever the `fxPixel` setting says (when the effects already draw through the world pen, that pen gives the look).
 *
 * One look for every `ghost` effect with `step: true`: skill dashes and step-ins (effects.ts skillDash), the ports'
 * afterimages (sampleArt/port.ts), the bout's fast moves and qinggong (render/lastShot.ts frames), the sprint.
 * Drawn like the sample, under the figures (the sample paints them before the body): a ground pass of its own, after
 * the ground effects, so a runner heading toward the camera is not covered by its own copies.
 * Wiring: effects.ts `case 'ghost'` hands every ghost to `drawGhost` (one line), which leaves them to that pass.
 */
import { artH, artW, artX, artY } from '../pixel';
import { registerRenderPass } from '../passes/registry';
import { fxPixelOn, fxStep, ghostOf, PixelPen } from './pixelRules';
import type { Fx } from './registry';

type Ghost = Extract<Fx, { type: 'ghost' }>;

const pen = new PixelPen();

/** The sample's alpha steps over a ghost's life (`k` 0..1). */
export const afterimageAlpha = (k: number): number => (k < 0.35 ? 1 : k < 0.7 ? 0.2 : 0.08);

/** Draws a stepped ghost at (`x`, `y`) (screen art px, the world offset in) at `k` of its life, in the sample look. */
export function drawAfterimage(g: CanvasRenderingContext2D, f: Ghost, x: number, y: number, k: number): void {
  if (k >= 1) return;
  const own = !fxPixelOn();
  const p = own ? pen.begin(g) : g;
  p.save();
  p.globalAlpha = afterimageAlpha(k);
  const img = ghostOf(f.img);
  const rx = Math.round(x);
  const ry = Math.round(y);
  if (f.flip) {
    p.translate(rx, 0);
    p.scale(-1, 1);
    p.drawImage(img, artX(f.img) - f.ax, ry - f.ay + artY(f.img), artW(f.img), artH(f.img));
  } else p.drawImage(img, rx - f.ax + artX(f.img), ry - f.ay + artY(f.img), artW(f.img), artH(f.img));
  p.restore();
  if (own) pen.end();
}

/**
 * Every `ghost` effect, as the user decided (10-11): a stepped ghost (dashes, step-ins, bout moves, qinggong, sprint)
 * in the sample look; the rest (the swing smear, the ride ghosts) no longer drawn. Returns true: nothing else to draw.
 */
export function drawGhost(_g: CanvasRenderingContext2D, _f: Ghost, _x: number, _y: number, _k: number): boolean {
  return true;
}

registerRenderPass({
  id: 'afterimages',
  layer: 'ground',
  order: 912,
  draw(f) {
    for (const e of f.effects.fx) {
      if (e.type !== 'ghost' || !e.step || e.t < 0) continue;
      drawAfterimage(f.g, e, e.x + f.ox, e.y + f.oy, fxStep(e.t) / e.life);
    }
  },
});
